import { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login';
import ConfigList from './pages/ConfigList';
import { CutoutQaTab } from './pages/CutoutQaTab';
import Editor from './pages/Editor';
import FeedsTab from './pages/FeedsTab';
import TimelineTab from './pages/TimelineTab';
import Stores from './pages/Stores';
import Platform from './pages/Platform';
import AdminUsage from './pages/AdminUsage';
import SetPassword from './pages/SetPassword';
import RequireAuth from './components/RequireAuth';
import RequireAdmin from './components/RequireAdmin';
import Landing from './components/Landing';
import { api } from './api/client';
import { useAuth } from './store/auth';
import { useStoreSlice } from './store/storeRegistry';

export default function App() {
  const setMe = useAuth((s) => s.setMe);

  // Bootstrap identity once on load. /api/me is in the client's 401 skip-list,
  // so an anonymous visitor resolves to anon (no hard redirect) and the guards
  // route them to /login. Reconcile the selected store to an accessible one
  // BEFORE flipping to authed, so guarded pages mount with a valid store
  // (no widforss 403-flash for clients who don't own it).
  useEffect(() => {
    let alive = true;
    api
      .me()
      .then((me) => {
        if (!alive) return;
        const { selectedStoreId, setSelectedStoreId } = useStoreSlice.getState();
        if (!me.stores.some((s) => s.id === selectedStoreId)) {
          setSelectedStoreId(me.stores[0]?.id ?? '');
        }
        setMe(me);
      })
      .catch(() => alive && setMe(null));
    return () => {
      alive = false;
    };
  }, [setMe]);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route element={<RequireAuth />}>
        <Route path="/" element={<ConfigList />} />
        {/* Cutouts tab retired — merged into Cutout QA (2026-06-08). */}
        <Route path="/cutouts" element={<Navigate to="/cutout-qa" replace />} />
        {/* D-7 reversal (2026-08-28): the QA suite is CLIENT-FACING — agencies
            self-serve QA for their own stores; the backend scopes every QA
            route per store (require_store_access). Admin-only: user management,
            store create/delete, cross-tenant views, the SnapEdit lane. */}
        <Route path="/cutout-qa" element={<CutoutQaTab />} />
        <Route path="/stores" element={<Stores />} />
        <Route element={<RequireAdmin />}>
          {/* /admin/users retired in favour of the org-aware Platform view (Task 10). */}
          <Route path="/admin/users" element={<Navigate to="/platform" replace />} />
          <Route path="/admin/usage" element={<AdminUsage />} />
          <Route path="/platform" element={<Platform />} />
        </Route>
        <Route path="/feeds" element={<FeedsTab />} />
        <Route path="/timeline" element={<TimelineTab />} />
        <Route path="/edit/:name" element={<Editor />} />
        {/* Nested inside RequireAuth so identity is resolved before Landing picks a role-based target. */}
        <Route path="*" element={<Landing />} />
      </Route>
    </Routes>
  );
}
